import type { TabbedSelectComboboxTab } from "./types";

/**
 * Smart-tab selection, ported from `withDropdownSmartTabs`/`DropdownSmartTabs`: when the currently
 * selected tab has no items (e.g. after a search narrows it to nothing) but some other tab still has
 * results, move forward to the first non-empty tab. Returns the id of the tab that should be active.
 *
 * `currentId` is the id the consumer last selected; when it is missing from `tabs` (or undefined) we
 * fall back to the first tab. When every tab is empty we keep the current selection (nothing to move
 * to), matching the legacy HOC which bails out when no tab has results.
 */
export const resolveSmartSelectedTabId = (
    tabs: readonly TabbedSelectComboboxTab[],
    currentId: string | undefined,
): string | undefined => {
    if (tabs.length === 0) {
        return undefined;
    }

    const currentIndex = tabs.findIndex((tab) => tab.id === currentId);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    if (tabs[safeIndex].items.length > 0) {
        return tabs[safeIndex].id;
    }

    const firstNonEmpty = tabs.find((tab) => tab.items.length > 0);

    return firstNonEmpty ? firstNonEmpty.id : tabs[safeIndex].id;
};
